import { useInfiniteQuery } from "@tanstack/react-query";
import { useNavigate, useSearch } from "@tanstack/react-router";
import type { ActorType } from "@app-factory/shared";
import { api } from "@/lib/api";
import { cs } from "@/i18n/cs";
import { Btn, Chip, EmptyState, FilterPill, PageHeader, type Tone } from "@/design/primitives";
import { DataTable, type Column } from "@/design/DataTable";
import { relativeTime } from "@/lib/format";
import type { AuditEventDTO } from "@app-factory/shared";

/** Hodnoty, které smí stát v `?actor=` — validuje je routa. */
export const ACTOR_FILTERS = ["HUMAN", "AI", "WORKER", "SYSTEM"];

const FILTERS: { key: ActorType | "ALL"; label: string }[] = [
  { key: "ALL", label: cs.common.all },
  { key: "HUMAN", label: "HUMAN" },
  { key: "AI", label: "AI" },
  { key: "WORKER", label: "WORKER" },
  // SYSTEM chybělo: události sweeperů se daly z auditu vyfiltrovat jen ručně v URL
  { key: "SYSTEM", label: "SYSTEM" },
];

const ACTOR_TONE: Record<ActorType, Tone> = {
  HUMAN: "amber",
  AI: "cyan",
  WORKER: "green",
  SYSTEM: "violet",
};

/** Activity / Audit — filtrovatelná tabulka dle /Design (sekce AUDIT). */
export function AuditPage() {
  const navigate = useNavigate();
  const search = useSearch({ from: "/shell/audit" }) as { actor?: string };
  const filter = (search.actor as ActorType | undefined) ?? "ALL";

  const query = useInfiniteQuery({
    queryKey: ["audit", "page", filter],
    queryFn: ({ pageParam }) =>
      api.audit({
        actorType: filter === "ALL" ? undefined : filter,
        cursor: pageParam,
        limit: 50,
      }),
    initialPageParam: undefined as string | undefined,
    getNextPageParam: (last) => last.nextCursor ?? undefined,
  });

  const events = query.data?.pages.flatMap((p) => p.events) ?? [];

  const columns: Column<AuditEventDTO>[] = [
    {
      key: "ts",
      label: cs.audit.time,
      width: "90px",
      cell: (e) => <span style={{ color: "var(--text-tertiary)" }}>{relativeTime(e.ts)}</span>,
    },
    {
      key: "actor",
      label: cs.audit.actor,
      width: "110px",
      cell: (e) => <Chip tone={ACTOR_TONE[e.actorType]}>{e.actorType}</Chip>,
    },
    {
      key: "action",
      label: cs.audit.action,
      width: "minmax(200px, 250px)",
      primary: true,
      cell: (e) => <span style={{ letterSpacing: ".04em" }}>{e.action}</span>,
    },
    {
      key: "project",
      label: cs.audit.project,
      width: "minmax(120px, 150px)",
      cell: (e) => <span style={{ color: "var(--accent)" }}>{e.projectName ?? cs.common.none}</span>,
    },
    {
      key: "summary",
      label: cs.audit.detail,
      cell: (e) => (
        <>
          {e.summary}
          {e.reason ? <span style={{ color: "var(--text-tertiary)" }}> · {e.reason}</span> : null}
        </>
      ),
    },
    {
      key: "link",
      label: cs.audit.link,
      width: "minmax(120px, 170px)",
      cell: (e) => <span style={{ color: "var(--text-tertiary)" }}>{e.link ?? ""}</span>,
    },
  ];

  return (
    <div className="af-animate-fade">
      <PageHeader
        title={cs.audit.title}
        kicker="HISTORIE"
        lead={cs.audit.subtitle}
        right={FILTERS.map((f) => (
          <FilterPill
            key={f.key}
            active={filter === f.key}
            testId={`audit-filter-${f.key}`}
            onClick={() =>
              void navigate({ to: "/audit", search: f.key === "ALL" ? {} : { actor: f.key } })
            }
          >
            {f.label}
          </FilterPill>
        ))}
      />

      <div className="mt-5">
        <DataTable
          columns={columns}
          rows={events}
          rowKey={(e) => e.id}
          testId="audit-table"
          stackBelow={1024}
          empty={query.isLoading ? null : <EmptyState title={cs.audit.empty} />}
        />
      </div>

      {query.hasNextPage ? (
        <div className="mt-4 flex justify-center">
          <Btn onClick={() => void query.fetchNextPage()} disabled={query.isFetchingNextPage}>
            {cs.audit.loadMore}
          </Btn>
        </div>
      ) : null}
    </div>
  );
}
